import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import moment from 'moment-timezone';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { useDispatch, useSelector } from 'react-redux';
import {
  createTour,
  fetchInboxMetaData,
  fetchMetaData,
  fetchPropertyByApartmentIdOffPlatform,
  fetchTimeSlots,
  fetchTourConfirmation
} from '../../Redux/Actions/InboxV1';
import {
  setGlobalError,
  setSuccessInfo
} from '../../Redux/Reducer/GlobalError';
import { setMessagesEmpty } from '../../Redux/Reducer/InboxV1';
import { DAYS_LIST, MONTHS_LIST } from '../../utils';
import TourBookingModal from './TourBookingModal';
import DateInput from '../DateInput';
import { getListOfAvailableTourTypesForUI } from './utils';

const CONTACT_TYPES = [
  { key: 'Call', value: 'call' },
  { key: 'Text', value: 'text' },
  { key: 'Email', value: 'email' }
];

const TOUR_CONFIRMATION_POLL_INTERVAL_MS = 5000;
// 60 attempts × 5s = 5 min hard cap so an indefinitely-pending booking
// can't keep firing GET /api/tours/confirmation forever.
const TOUR_CONFIRMATION_POLL_MAX_ATTEMPTS = 60;

const formatUnitLabel = unit => {
  if (!unit) return 'No unit selected';
  if (unit.unitName === 'Unit Placeholder') {
    return '$0 - 0bd 0ba - 0 sqft - Placeholder Unit';
  }
  const rent = unit?.rent?.min ?? 0;
  const beds = unit?.bedRooms ?? 0;
  const baths = unit?.bathRooms ?? 0;
  const sqft = unit?.sqft ?? 0;
  return `$${rent} - ${beds}bd ${baths}ba - ${sqft} sqft - ${
    unit?.unitName || ''
  }`;
};

const truncate = (str, len = 7) =>
  str?.length > len ? str.substring(0, len) + '...' : str;

const TourSchedulingWidget = ({
  handleClose,
  schedule,
  fetchTourBack,
  interest,
  apartmentData,
  propertyData = [],
  leadData,
  leadId,
  unitSelected,
  lead
}) => {
  const { availableTourTypes } = useSelector(state => state.Apartment);
  const {
    slotsLoading,
    slots,
    dates,
    tourId,
    isTourCreated,
    tourConfirmationData,
    tourConfirmationFetchFailed,
    offPlatformPropertyByApi,
    slotsMessage,
    slotsErrorMessage
  } = useSelector(state => state.slotsData);
  const dispatch = useDispatch();
  const tourConfirmationAttemptsRef = useRef(0);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isZumaTour, setZumaTour] = useState(false);
  const [offPlatformProperty, setOffPlatformProperty] = useState([
    ...propertyData
  ]);
  const [tourList, setTourList] = useState([]);
  const [original, setOriginal] = useState(true);
  const [tourTypeValue, setTourTypeValue] = useState('');
  const [contactTypeValue, setContactTypeValue] = useState('');
  const [unitsData, setUnitsData] = useState([]);
  const [nextStep, setNextStep] = useState(false);
  const [timeSelected, setTimeSelected] = useState(null);
  const [timeSlotsActive, setTimeSlotsActive] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [openSummary, setOpenSummary] = useState(false);
  const [openoffPlatformOn, setOffPlatformNo] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [community, setCommunity] = useState({ interest });
  const [newUnit, setNewUnit] = useState(unitSelected);
  const [time, setTime] = useState('07:30');
  const [website, setWebsite] = useState(interest?.website ?? '');

  useEffect(() => {
    schedule?.forEach(check => {
      if (check.isZumaTour) {
        setZumaTour(true);
        setOriginal(false);
      }
    });
    dispatch(fetchInboxMetaData({ leadId }));
  }, []);

  useEffect(() => {
    if (dates?.length > 0) {
      setSelectedDate(new Date(moment(dates[0])?.format('YYYY MM DD')));
    }
  }, []);

  useEffect(() => {
    setOffPlatformProperty([...propertyData]);
  }, [propertyData]);

  useEffect(() => {
    const availableTourTypesByApartment =
      getListOfAvailableTourTypesForUI(availableTourTypes);
    if (apartmentData?.length > 0 && leadData) {
      const apartment = apartmentData?.find(
        a => a._id === leadData?.apartment?._id
      );
      if (apartment) {
        setTourList(availableTourTypesByApartment);
      }
    } else {
      setCommunity({ name: 'Placeholder Community', _id: '123' });
      setTourList(availableTourTypesByApartment);
    }
  }, [apartmentData, original]);

  useEffect(() => {
    if (leadId && tourTypeValue && original) {
      dispatch(
        fetchTimeSlots({
          communityId: lead?.apartment?._id,
          leadId,
          tourType: tourTypeValue === 'selfguided' ? 'inperson' : tourTypeValue
        })
      );
    }
  }, [tourTypeValue, leadId]);

  useEffect(() => {
    if (slotsMessage) {
      dispatch(setSuccessInfo(slotsMessage));
      dispatch(setMessagesEmpty());
    }
  }, [slotsMessage]);

  useEffect(() => {
    if (slotsErrorMessage) {
      dispatch(setGlobalError(slotsErrorMessage));
      dispatch(setMessagesEmpty());
    }
  }, [slotsErrorMessage]);

  useEffect(() => {
    if (!slotsLoading && dates?.length > 0) {
      setSelectedDate(dates[0]);
    }
  }, [slotsLoading, dates]);

  useEffect(() => {
    if (offPlatformPropertyByApi?.length) {
      setOffPlatformProperty([...offPlatformPropertyByApi]);
    }
  }, [offPlatformPropertyByApi]);

  useEffect(() => {
    if (offPlatformProperty?.length > 0 && leadData) {
      const units = offPlatformProperty.filter(Boolean).map(p => ({
        label: formatUnitLabel(p),
        value: formatUnitLabel(p),
        _id: p._id
      }));
      setUnitsData(units);
    }
  }, [offPlatformProperty]);

  // Reset the attempts counter only when starting a fresh polling cycle. If we
  // reset inside the polling effect, a stale .fulfilled landing after a
  // .rejected could clear the failure flag, re-trigger the effect, and grant
  // another full cap window — bypassing the limit.
  useEffect(() => {
    if (nextStep) tourConfirmationAttemptsRef.current = 0;
  }, [nextStep]);

  useEffect(() => {
    if (!isTourCreated || !tourId || !nextStep) return;
    if (tourConfirmationData?.confirmed || tourConfirmationData?.errorType)
      return;
    if (tourConfirmationFetchFailed) return;
    if (
      tourConfirmationAttemptsRef.current >= TOUR_CONFIRMATION_POLL_MAX_ATTEMPTS
    )
      return;

    const intervalId = setInterval(() => {
      if (
        tourConfirmationAttemptsRef.current >=
        TOUR_CONFIRMATION_POLL_MAX_ATTEMPTS
      ) {
        clearInterval(intervalId);
        return;
      }
      tourConfirmationAttemptsRef.current += 1;
      dispatch(fetchTourConfirmation({ id: tourId }));
    }, TOUR_CONFIRMATION_POLL_INTERVAL_MS);
    fetchTourBack();
    return () => clearInterval(intervalId);
  }, [
    nextStep,
    isTourCreated,
    tourId,
    tourConfirmationData?.confirmed,
    tourConfirmationData?.errorType,
    tourConfirmationFetchFailed
  ]);

  useEffect(() => {
    const selectedDay = selectedDate
      ? moment(selectedDate).format('YYYY-MM-DD')
      : moment(dates?.[0]).format('YYYY-MM-DD');
    if (slots?.length > 0) {
      const filtered = slots
        .filter(val => val.includes(selectedDay))
        .map(e => e.slice(0, 19));
      setTimeSlotsActive(filtered);
    }
  }, [selectedDate, slots]);

  useEffect(() => {
    setCommunity(interest);
    setNewUnit(unitSelected);
  }, [original]);

  useEffect(() => {
    if (!original) {
      if (isTourCreated) setShowSuccess(true);
    } else {
      if (tourConfirmationData?.confirmed) {
        setShowSuccess(true);
      } else if (tourConfirmationData?.errorType) {
        setShowError(true);
        if (tourConfirmationData.errorType === 'TIME_NOT_AVAILABLE') {
          setErrorText('Selected time slot is not available.');
        }
      }
    }
  }, [nextStep, isTourCreated, tourConfirmationData]);

  const selectCustomDate = item => {
    setSelectedDate(moment(item).format('YYYY-MM-DD'));
  };

  const handleCommunity = (e, newValue) => {
    setCommunity({ _id: newValue?._id, name: newValue?.name });
    setNewUnit('');
    setWebsite(newValue?.website);
    dispatch(
      fetchPropertyByApartmentIdOffPlatform({ apartmentId: newValue?._id })
    );
  };

  const handleUnit = (e, newValue) => {
    setNewUnit({ _id: newValue?._id, value: newValue?.value });
  };

  const createTourPayload = confirmedDate => ({
    confirmedDate,
    leadId,
    aptId: community?._id,
    unitName: newUnit?.unitName,
    tourType: tourTypeValue,
    isZumaTour: original,
    contactType: contactTypeValue
  });

  const handleNext = async () => {
    setNextStep(true);
    await dispatch(createTour({ data: createTourPayload(timeSelected) }));
    await dispatch(fetchMetaData({ leadId }));
  };

  const date = moment(selectedDate).format().slice(0, 11) + time + ':00';

  const handleSave = async () => {
    setNextStep(true);
    setOpenSummary(false);
    await dispatch(createTour({ data: createTourPayload(date) }));
    await dispatch(fetchMetaData({ leadId }));
  };

  const handleBack = () => {
    setNextStep(false);
    setErrorText('');
    setShowSuccess(false);
    setShowError(false);
  };

  const handleWidgetClose = () => {
    setErrorText('');
    setShowSuccess(false);
    setShowError(false);
    handleClose();
  };

  const bookingStatus = showSuccess ? 'success' : showError ? 'error' : '';
  const isDisabledOverlay = openSummary || nextStep || isZumaTour;

  const CustomDateInput = forwardRef(({ value, onClick }, ref) => (
    <Button
      variant="outlined"
      size="small"
      startIcon={<CalendarTodayIcon />}
      onClick={onClick}
      ref={ref}
    >
      {value || 'Select Date'}
    </Button>
  ));

  const memoisedCallGenerateDates = useCallback(() => {
    const days = Array.from({ length: 60 }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() + i);
      return d;
    });

    return days.map((item, i) => {
      const formatted = moment(item).format('YYYY-MM-DD');
      const isAvailable = !original || dates?.includes(formatted);
      const isSelected = formatted == selectedDate;
      return (
        <Tooltip
          key={i}
          title={!isAvailable ? 'No available slots on this date' : ''}
          arrow
        >
          <span style={{ flexShrink: 0 }}>
            <Button
              id="date-select"
              title="date-select"
              onClick={() => selectCustomDate(item)}
              disabled={!isAvailable}
              variant={isSelected ? 'contained' : 'outlined'}
              color={isSelected ? 'primary' : 'inherit'}
              sx={{
                minWidth: 56,
                px: 1,
                py: 0.5,
                mr: 0.5,
                flexShrink: 0,
                flexDirection: 'column',
                lineHeight: 1.2
              }}
            >
              <Typography variant="caption" component="span">
                {DAYS_LIST[item.getDay()].substring(0, 3)}
              </Typography>
              <Typography variant="body2" component="span" fontWeight={600}>
                {item.getDate()} {MONTHS_LIST[item.getMonth()].substring(0, 3)}
              </Typography>
            </Button>
          </span>
        </Tooltip>
      );
    });
  }, [dates, selectCustomDate, selectedDate, original]);

  const renderTourTypeSelect = () => (
    <TextField
      select
      label="Tour type"
      size="small"
      fullWidth
      value={tourTypeValue}
      onChange={e => setTourTypeValue(e.target.value)}
    >
      {tourList?.map((item, i) => (
        <MenuItem key={i} value={item.value}>
          {item.label}
        </MenuItem>
      ))}
    </TextField>
  );

  const renderContactTypeSelect = () => (
    <TextField
      select
      label="Contact type"
      size="small"
      fullWidth
      value={contactTypeValue}
      onChange={e => setContactTypeValue(e.target.value)}
    >
      {CONTACT_TYPES.map((item, i) => (
        <MenuItem key={i} value={item.value}>
          {item.key}
        </MenuItem>
      ))}
    </TextField>
  );

  const renderSlotPicker = () => (
    <Box sx={{ mt: 2 }}>
      <Card variant="outlined">
        {!slotsLoading ? (
          <Stack>
            <Stack
              direction="row"
              sx={{
                overflow: 'auto',
                p: 1,
                borderBottom: 1,
                borderColor: 'divider'
              }}
            >
              {memoisedCallGenerateDates()}
            </Stack>

            {/* Time slots */}
            <Box sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Available time slots
              </Typography>
              <Stack
                direction="row"
                flexWrap="wrap"
                gap={1}
                sx={{ maxHeight: 160, overflow: 'auto', mt: 1 }}
              >
                {!tourTypeValue ? (
                  <Typography variant="body2" color="text.secondary">
                    Select tour type to view available slots
                  </Typography>
                ) : timeSlotsActive.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No slots available
                  </Typography>
                ) : (
                  timeSlotsActive.map((t, i) => (
                    <Chip
                      key={i}
                      id="time-select"
                      label={moment(t).format('LT')}
                      onClick={() => setTimeSelected(t)}
                      color={timeSelected === t ? 'primary' : 'default'}
                      variant={timeSelected === t ? 'filled' : 'outlined'}
                    />
                  ))
                )}
              </Stack>
            </Box>
          </Stack>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}
      </Card>
    </Box>
  );

  const renderOnPlatformForm = () => (
    <DialogContent>
      <Stack direction="row" spacing={2}>
        <Tooltip
          title={
            leadData?.preference?.interest
              ? 'Community is set from lead preferences'
              : leadData?.apartment?._id
                ? 'Community is set from the linked apartment'
                : leadData?.tenant?.name &&
                    leadData?.tenant?.name !== 'Skylight'
                  ? 'Community is locked for this tenant'
                  : ''
          }
          arrow
        >
          <span style={{ width: '100%' }}>
            <Autocomplete
              id="on-platform-preferred-community"
              disabled={
                leadData?.preference?.interest ||
                leadData?.apartment?._id ||
                (leadData?.tenant?.name &&
                  leadData?.tenant?.name !== 'Skylight')
              }
              fullWidth
              disablePortal
              value={interest}
              options={apartmentData}
              getOptionLabel={option => option.name}
              onChange={handleCommunity}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Preferred community"
                  placeholder="Select preferred community"
                />
              )}
            />
          </span>
        </Tooltip>
        <Tooltip
          title={
            unitSelected
              ? "Unit is automatically selected based on the lead's community"
              : 'No unit is associated with this lead'
          }
          arrow
        >
          <span style={{ width: '100%' }}>
            <Autocomplete
              disabled
              fullWidth
              disablePortal
              options={unitsData || []}
              value={
                unitSelected
                  ? formatUnitLabel(unitSelected)
                  : 'No unit selected'
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Preferred unit"
                  placeholder="Select preferred unit"
                />
              )}
            />
          </span>
        </Tooltip>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        {renderTourTypeSelect()}
        {renderContactTypeSelect()}
      </Stack>

      {renderSlotPicker()}
    </DialogContent>
  );

  const renderOffPlatformForm = () => (
    <DialogContent>
      <Stack direction="row" spacing={2}>
        <Autocomplete
          fullWidth
          value={community}
          disableClearable
          options={apartmentData || []}
          getOptionLabel={option => option.name}
          onChange={handleCommunity}
          renderInput={params => (
            <TextField
              {...params}
              label="Preferred community"
              required={!community}
              placeholder="Select preferred community"
            />
          )}
        />
        <Autocomplete
          fullWidth
          disablePortal
          disableClearable
          value={
            unitSelected ? formatUnitLabel(unitSelected) : 'No unit selected'
          }
          options={unitsData || []}
          onChange={handleUnit}
          renderInput={params => (
            <TextField
              {...params}
              label="Preferred unit"
              required={!newUnit}
              placeholder="Select preferred unit"
            />
          )}
        />
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        {renderTourTypeSelect()}
        {renderContactTypeSelect()}
      </Stack>

      <Box sx={{ mt: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {website && (
            <Link
              href={website}
              target="_blank"
              rel="noopener noreferrer"
              variant="caption"
            >
              View Website
            </Link>
          )}
        </Stack>
        <Card variant="outlined" sx={{ mt: 1 }}>
          <Stack>
            <Stack
              direction="row"
              sx={{
                overflow: 'auto',
                p: 1,
                borderBottom: 1,
                borderColor: 'divider'
              }}
            >
              {memoisedCallGenerateDates()}
            </Stack>
            <Box sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Select time
              </Typography>
              <TextField
                id="time"
                type="time"
                label="Tour time"
                size="small"
                defaultValue="07:30"
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  step: 300,
                  title: 'off-platform-time-select'
                }}
                sx={{ mt: 1, display: 'block' }}
                onChange={e => setTime(e.target.value)}
                title="off-platform-time-select"
              />
            </Box>
          </Stack>
        </Card>
      </Box>
    </DialogContent>
  );

  const renderSummary = () => (
    <DialogContent>
      <Stack alignItems="center" sx={{ mt: 4 }}>
        <Card variant="outlined" sx={{ width: 300 }}>
          <CardContent>
            <Typography variant="subtitle1" align="center" gutterBottom>
              Appointment
            </Typography>
            <Typography variant="body2" align="center" gutterBottom>
              {moment(date).format('h:mm a, ddd, DD-MMM-YYYY')}
            </Typography>
            <Stack spacing={1} sx={{ mt: 2 }}>
              {[
                ['Community', truncate(community?.name)],
                [
                  'Client',
                  lead?.tenant?.name ? truncate(lead.tenant.name) : '-'
                ],
                [
                  'Unit',
                  newUnit
                    ? truncate(newUnit?.value)
                    : formatUnitLabel(unitSelected)?.substring(0, 20) + '...'
                ],
                ['Contact', contactTypeValue],
                ['Tour Type', tourTypeValue]
              ].map(([label, value]) => (
                <Stack
                  key={label}
                  direction="row"
                  justifyContent="space-between"
                >
                  <Typography variant="body2" fontWeight={500}>
                    {label}:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {value}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </CardContent>
        </Card>

        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 3 }}>
          <WarningAmberIcon color="warning" />
          <Typography variant="body2">
            Have you booked the tour on the{' '}
            {website ? (
              <Link href={website} target="_blank" rel="noopener noreferrer">
                website
              </Link>
            ) : (
              <Link
                component="button"
                onClick={() => alert('website not available')}
              >
                website
              </Link>
            )}
            ?
          </Typography>
        </Stack>

        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          <Button variant="contained" onClick={handleSave}>
            Yes
          </Button>
          <Button variant="text" onClick={() => setOffPlatformNo(true)}>
            No
          </Button>
        </Stack>

        {openoffPlatformOn && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Please book the tour on client's website
          </Alert>
        )}
      </Stack>
    </DialogContent>
  );

  const renderNavActions = ({
    isFirstStep,
    onForward,
    onBack,
    forwardDisabled,
    forwardDisabledReason,
    forwardLabel = 'Next'
  }) => (
    <DialogActions>
      {!isFirstStep ? (
        <Button onClick={onBack} startIcon={<ArrowBackIcon />}>
          Back
        </Button>
      ) : (
        <Box />
      )}
      {isFirstStep && (
        <Tooltip title={forwardDisabled ? forwardDisabledReason : ''} arrow>
          <span>
            <Button
              id="btn-next"
              variant="contained"
              onClick={onForward}
              disabled={forwardDisabled}
            >
              {forwardLabel}
            </Button>
          </span>
        </Tooltip>
      )}
    </DialogActions>
  );

  return (
    <Dialog
      onClose={handleWidgetClose}
      open={true}
      maxWidth="md"
      fullWidth
      className="tour-schedule-popup"
    >
      <DialogTitle>
        Schedule Tour
        <IconButton
          id="btn-close"
          aria-label="close"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Tooltip
        title={
          isDisabledOverlay
            ? isZumaTour
              ? 'This lead already has a Zuma-managed tour'
              : 'Tour booking is in progress'
            : ''
        }
        arrow
      >
        <Box
          sx={
            isDisabledOverlay ? { pointerEvents: 'none', opacity: 0.7 } : null
          }
        >
          <Box sx={{ px: 3 }}>
            <ToggleButtonGroup
              value={original ? 'on' : 'off'}
              exclusive
              onChange={(e, val) => {
                if (val !== null) setOriginal(val === 'on');
              }}
              size="small"
            >
              <ToggleButton value="on">On Platform</ToggleButton>
              <ToggleButton value="off">Off Platform</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      </Tooltip>

      {original ? (
        <>
          {!nextStep ? (
            renderOnPlatformForm()
          ) : (
            <DialogContent>
              <TourBookingModal
                handleBack={handleBack}
                status={bookingStatus}
              />
            </DialogContent>
          )}
          {renderNavActions({
            isFirstStep: !nextStep,
            onForward: handleNext,
            onBack: handleBack,
            forwardDisabled: !tourTypeValue || timeSelected === null,
            forwardDisabledReason: !tourTypeValue
              ? 'Select a tour type'
              : timeSelected === null
                ? 'Select a time slot'
                : '',
            forwardLabel: 'Book Tour'
          })}
        </>
      ) : (
        <>
          {openSummary ? (
            renderSummary()
          ) : !nextStep ? (
            renderOffPlatformForm()
          ) : (
            <DialogContent>
              <TourBookingModal
                handleBack={handleBack}
                status={bookingStatus}
                errorText={errorText}
              />
            </DialogContent>
          )}
          {renderNavActions({
            isFirstStep: !openSummary && !nextStep,
            onForward: () => setOpenSummary(true),
            onBack: handleBack,
            forwardDisabled:
              !tourTypeValue || !community || !newUnit || !contactTypeValue,
            forwardDisabledReason: !tourTypeValue
              ? 'Select a tour type'
              : !community
                ? 'Select a community'
                : !newUnit
                  ? 'Select a unit'
                  : !contactTypeValue
                    ? 'Select a contact type'
                    : '',
            forwardLabel: 'Review'
          })}
        </>
      )}
    </Dialog>
  );
};

export default TourSchedulingWidget;
